import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';

import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, FormBuilder, ReactiveFormsModule, FormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { MatSelectModule } from '@angular/material/select';

@Component({
  selector: 'app-playlist-dialog',
  imports: [
    MatDialogModule,
    MatInputModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    DragDropModule,
    MatSelectModule,
    FormsModule,
    MatIconModule,
    MatButtonModule,
    MatListModule
],
  templateUrl: './playlist-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './playlist-dialog.component.scss',
})
export class PlaylistDialogComponent {
  form!: FormGroup;

  mediaLibrary: any[] = []
  playlistStack: any[] = []

  totalDuration: number = 0;

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<PlaylistDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {

    this.mediaLibrary = data.mediaList;

  }

  ngOnInit() {

    this.form = this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]]
    });

    if (this.data?.playlist) {

      const playlist = this.data.playlist;

      this.form.patchValue({
        name: playlist.name
      });

      const contents = playlist?.composition_contents || [];

      this.playlistStack = contents
        .sort((a: any, b: any) => a.sort_order - b.sort_order)
        .map((item: any) => {

          const media = item.content_details;

          // Use title from content_details.title instead of filename
          if (media.title) {
            media.name = media.title;
          }

          const durationParts = item.duration.split(':');

          const hours = parseInt(durationParts[0], 10) || 0;
          const minutes = parseInt(durationParts[1], 10) || 0;
          const seconds = parseInt(durationParts[2], 10) || 0;

          const totalSeconds = hours * 3600 + minutes * 60 + seconds;

          return {
            ...media,
            duration: totalSeconds,
            durationDisplay: this.secondsToMMSS(totalSeconds),
            fit: item.fit_type,
            composition_id: item.id
          };

        });

      this.calculateDuration();

    }

  }

  drop(event: CdkDragDrop<any[]>) {

    if (event.previousContainer === event.container) {

      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);

    } else {

      const item = { ...event.previousContainer.data[event.previousIndex], fit: 'fit' }

      event.container.data.splice(event.currentIndex, 0, item)

    }

    this.calculateDuration();

  }

  removeMedia(index: number) {

    this.playlistStack.splice(index, 1)

    this.calculateDuration();

  }

  calculateDuration() {

    this.totalDuration = this.playlistStack.reduce((sum, item) => {
      return sum + (item.duration || 10)
    }, 0)

  }

  save() {

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    // duration validation
    const hasInvalidDuration = this.playlistStack.some(
      item => (item.duration || 0) < 5
    );

    if (hasInvalidDuration) {
      this.playlistStack.forEach(item => {
        if ((item.duration || 0) < 5) {
          item.durationError = true;
        }
      });
      return;
    }

    const composition_contents = this.playlistStack.map((media, index) => {

      const durationSeconds = media.duration || 10;

      const durationFormatted = `00:00:${durationSeconds
        .toString()
        .padStart(2, '0')}`;

      return {
        id: media.composition_id || 0,
        composition: 0,
        content: media.id,
        content_zone: 1,
        duration: durationFormatted,
        sort_order: index + 1,
        content_details: media,
        composition_duration: {
          hours: 0,
          minutes: 0,
          seconds: durationSeconds
        },
        fit_type: media.fit || 'fit'
      };

    });

    const payload = {
      name: this.form.value.name,
      layout: 1,
      muted_zones: [],
      composition_contents: composition_contents
    };

    this.dialogRef.close(payload);

  }

  addToPlaylist(selectedItems: any[]) {

    selectedItems.forEach(item => {

      const media = item.value;

      let duration = 10; // default for images

      if (media.type === '.mp4') {

        if (media.duration) {

          if (typeof media.duration === 'string') {
            const parts = media.duration.split(':');
            duration = parseInt(parts[2] || '0', 10);
          } else {
            duration = media.duration;
          }

        }

      }

      const newItem = {
        ...media,
        fit: 'fit',
        duration: duration,
        durationDisplay: this.secondsToMMSS(duration),
        durationError: duration < 5
      };

      this.playlistStack.push(newItem);

    });

    this.calculateDuration();
  }

  secondsToMMSS(seconds: number): string {

    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;

    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }


  mmssToSeconds(value: string): number {

    if (!value) return 10;

    const parts = value.split(':');

    let minutes = parseInt(parts[0] || '0', 10);
    let seconds = parseInt(parts[1] || '0', 10);

    if (seconds > 59) {
      minutes += Math.floor(seconds / 60);
      seconds = seconds % 60;
    }

    return minutes * 60 + seconds;

  }

  updateDuration(media: any) {

    const totalSeconds = this.mmssToSeconds(media.durationDisplay);

    if (totalSeconds < 5) {

      media.durationError = true;
      media.duration = totalSeconds;
      media.durationDisplay = this.secondsToMMSS(totalSeconds);

    } else {

      media.durationError = false;
      media.duration = totalSeconds;
      media.durationDisplay = this.secondsToMMSS(totalSeconds);

    }

    this.calculateDuration();

  }

  hasInvalidDuration(): boolean {
    if (!this.playlistStack?.length) return false;

    return this.playlistStack.some((m: any) => (m.duration ?? 0) < 5);
  }

  formatDuration(seconds: number): string {

    if (!seconds) return '0s';

    if (seconds < 60) {
      return `${seconds}s`;
    }

    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;

      return `${minutes}m ${secs}s`;
    }

    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return `${hours}h ${minutes}m ${secs}s`;
  }
}
